import _Object$create from "@babel/runtime-corejs3/core-js-stable/object/create";
import _Object$assign from "@babel/runtime-corejs3/core-js-stable/object/assign";
import _forEachInstanceProperty from "@babel/runtime-corejs3/core-js-stable/instance/for-each";
import _concatInstanceProperty from "@babel/runtime-corejs3/core-js-stable/instance/concat";
import _Object$keys from "@babel/runtime-corejs3/core-js-stable/object/keys";
import _setTimeout from "@babel/runtime-corejs3/core-js-stable/set-timeout";
import EventEmitter from 'events';
import authenticate from '../AuthClient';
import { checkPushRequirements } from './pushRequirements';
import SegmentUpdateWorker from '../SegmentUpdateWorker';
import SplitUpdateWorker from '../SplitUpdateWorker';
import SSEClient from '../SSEClient';
import SSEHandlerFactory from '../SSEHandler';
import Backoff from '../../utils/backoff';
import { hashUserKey } from '../../utils/jwt/hashUserKey';
import logFactory from '../../utils/logger';
import { SECONDS_BEFORE_EXPIRATION, PUSH_DISCONNECT, PUSH_DISABLED, SSE_ERROR, SPLIT_KILL, SPLIT_UPDATE, SEGMENT_UPDATE, MY_SEGMENTS_UPDATE } from '../constants';
var log = logFactory('splitio-sync:push-manager');
/**
 * Factory of the push mode manager.
 *
 * @param {Object} context context of main client.
 * @param {Object | undefined} clientContexts map of user keys to client contexts to handle sync of MySegments. undefined for node.
 */

export default function PushManagerFactory(context, clientContexts
/* undefined for node */) {
  // No return a PushManager if PUSH mode is not supported.
  if (!checkPushRequirements(log)) return;
  var pushEmitter = new EventEmitter();
  var _context$get = context.get(context.constants.READINESS),
    splitsEventEmitter = _context$get.splits;
  var settings = context.get(context.constants.SETTINGS);
  var storage = context.get(context.constants.STORAGE);
  var sseClient = SSEClient.getInstance(settings);
  var sseHandler = SSEHandlerFactory(pushEmitter);
  sseClient.setEventHandler(sseHandler); // map of hashes to user keys, to dispatch MY_SEGMENTS_UPDATE events to the corresponding MySegmentsUpdateWorker

  var userKeyHashes = {}; // list of workers, used to stop them all together when push is disconnected

  var workers = []; // variable used on browser to reconnect only when a new client was added, saving some authentication and sse connections.

  var connectForNewClient = false;
  /** PushManager functions related to initialization */

  var reauthBackoff = new Backoff(connectPush, settings.scheduler.authRetryBackoffBase);
  var sseReconnectBackoff = new Backoff(sseClient.reopen, settings.scheduler.streamingReconnectBackoffBase);
  var timeoutId = 0;
  function scheduleTokenRefresh(issuedAt, expirationTime) {
    // clear scheduled token refresh if exists (needed when resuming PUSH)
    if (timeoutId) clearTimeout(timeoutId); // Set token refresh 10 minutes before expirationTime

    var delayInSeconds = expirationTime - issuedAt - SECONDS_BEFORE_EXPIRATION;
    log.info("Refreshing streaming token in ".concat(delayInSeconds, " seconds."));
    timeoutId = _setTimeout(connectPush, delayInSeconds * 1000);
  }
  function connectPush() {
    log.info('Connecting to push streaming.');
    var userKeys = clientContexts ? _Object$keys(clientContexts) : undefined;
    authenticate(settings, userKeys).then(function (authData) {
      // restart backoff retry counter for auth and SSE connections, due to HTTP/network errors
      reauthBackoff.reset();
      sseReconnectBackoff.reset(); // reset backoff in case SSE conexion has opened after a HTTP or network error.
      // emit PUSH_DISCONNECT if org is not whitelisted

      if (!authData.pushEnabled) {
        log.info('Streaming is not available. Switching to polling mode.');
        pushEmitter.emit(PUSH_DISCONNECT); // there is no need to close sseClient (it is not open on this scenario)

        return;
      } // don't open SSE connection if a new shared client was added, since it means that a new authentication is taking place

      if (userKeys && userKeys.length < _Object$keys(clientContexts).length) return; // Connect to SSE and schedule refresh token

      var decodedToken = authData.decodedToken;
      sseClient.open(authData);
      scheduleTokenRefresh(decodedToken.iat, decodedToken.exp);
    })["catch"](function (error) {
      var _context;
      sseClient.close(); // no harm if already disconnected

      pushEmitter.emit(PUSH_DISCONNECT); // no harm if `PUSH_DISCONNECT` was already notified
      // Handle HTTP error for invalid API Key

      if (error.statusCode === 401) {
        log.error("Fail to authenticate for push notifications, with error message: \"".concat(error.message, "\"."));
        return;
      } // Handle other HTTP and network errors

      var delayInMillis = reauthBackoff.scheduleCall();
      log.error(_concatInstanceProperty(_context = "Fail to authenticate for push notifications, with error message: \"".concat(error.message, "\". Attempting to reauthenticate in ")).call(_context, delayInMillis / 1000, " seconds."));
    });
  } // close SSE connection and cancel scheduled tasks

  function disconnectPush() {
    log.info('Disconnecting from push streaming.');
    sseClient.close();
    if (timeoutId) clearTimeout(timeoutId);
    reauthBackoff.reset();
    sseReconnectBackoff.reset();
  } // cancel scheduled fetch retries of Split, Segment, and MySegment Update Workers

  function stopWorkers() {
    _forEachInstanceProperty(workers).call(workers, function (worker) {
      return worker.backoff.reset();
    });
  }
  pushEmitter.on(PUSH_DISCONNECT, stopWorkers);
  /** Fallbacking due to STREAMING_DISABLED control event */

  pushEmitter.on(PUSH_DISABLED, function () {
    disconnectPush();
    pushEmitter.emit(PUSH_DISCONNECT); // no harm if polling already
  });
  /** Fallbacking due to SSE errors */

  pushEmitter.on(SSE_ERROR, function (error) {
    var _context2;

    // HTTP or network error in SSE connection
    // SSE connection is closed to avoid repeated errors due to retries
    sseClient.close(); // retries are handled via backoff algorithm

    var delayInMillis;
    if (error.parsedData && (error.parsedData.statusCode === 400 || error.parsedData.statusCode === 401)) {
      delayInMillis = reauthBackoff.scheduleCall(); // reauthenticate in case of token invalid or expired (when somehow refresh token was not properly executed)
    } else {
      delayInMillis = sseReconnectBackoff.scheduleCall(); // reconnect SSE for any other network or HTTP error
    }
    var errorMessage = error.parsedData && error.parsedData.message;
    log.error(_concatInstanceProperty(_context2 = "Fail to connect to streaming".concat(errorMessage ? ", with error message: \"".concat(errorMessage, "\"") : '', ". Attempting to reconnect in ")).call(_context2, delayInMillis / 1000, " seconds."));
    pushEmitter.emit(PUSH_DISCONNECT); // no harm if polling already
  });
  /** Functions related to synchronization (Queues and Workers in the spec) */

  var producer = context.get(context.constants.PRODUCER);
  var splitUpdateWorker = new SplitUpdateWorker(storage.splits, producer, splitsEventEmitter);
  workers.push(splitUpdateWorker);
  pushEmitter.on(SPLIT_KILL, splitUpdateWorker.killSplit);
  pushEmitter.on(SPLIT_UPDATE, splitUpdateWorker.put);
  if (clientContexts) {
    // browser
    pushEmitter.on(MY_SEGMENTS_UPDATE, function handleMySegmentsUpdate(parsedData, channel) {
      var userKeyHash = channel.split('_')[2];
      var userKey = userKeyHashes[userKeyHash];
      if (userKey && clientContexts[userKey]) {
        // check context since it can be undefined if client has been destroyed
        var mySegmentSync = clientContexts[userKey].get(context.constants.MY_SEGMENTS_CHANGE_WORKER, true);
        mySegmentSync && mySegmentSync.put(parsedData.changeNumber, parsedData.includesPayload ? parsedData.segmentList ? parsedData.segmentList : [] : undefined);
      }
    });
  } else {
    // node
    var segmentUpdateWorker = new SegmentUpdateWorker(storage.segments, producer);
    workers.push(segmentUpdateWorker);
    pushEmitter.on(SEGMENT_UPDATE, segmentUpdateWorker.put);
  }
  return _Object$assign(
  // Expose Event Emitter functionality and Event constants
  _Object$create(pushEmitter), {
    // Expose functionality for starting and stoping push mode:
    stop: function stop() {
      disconnectPush();
      stopWorkers(); // if we call `stopWorkers` inside `disconnectPush`, it would be called twice on a PUSH_DISABLED event, which anyway is not harmful.
    },
    // used in node
    start: connectPush,
    // used in browser
    startNewClient: function startNewClient(userKey, context) {
      var hash = hashUserKey(userKey);
      var storage = context.get(context.constants.STORAGE);
      var producer = context.get(context.constants.PRODUCER);
      if (!userKeyHashes[hash]) {
        userKeyHashes[hash] = userKey;
        connectForNewClient = true; // we must reconnect on start, to listen the channel for the new user key
      }
      var mySegmentSync = new SegmentUpdateWorker(storage.segments, producer);
      workers.push(mySegmentSync);
      context.put(context.constants.MY_SEGMENTS_CHANGE_WORKER, mySegmentSync); // Reconnects in case of a new client.
      // Run in next event-loop cycle to save authentication calls
      // in case the user is creating several clients in the current cycle.

      _setTimeout(function checkForReconnect() {
        if (connectForNewClient) {
          connectForNewClient = false;
          connectPush();
        }
      }, 0);
    },
    removeClient: function removeClient(userKey) {
      var hash = hashUserKey(userKey);
      delete userKeyHashes[hash];
    }
  });
}